import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Plus } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { MasterSmsTabs } from "@/components/master-sms-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useSmsAccounts, useToggleSmsAccount, useRechargeSmsAccount } from "@/hooks/queries/platform";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import type { SmsAccount } from "@/services/gpsdrive-sms-credits.service";

export const Route = createFileRoute("/master/sms")({
  component: () => (
    <MasterShell>
      <MasterSmsTabs />
      <SmsCreditsPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "SMS — Super Master — GPS Drive" }] }),
});

const currencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

function parseDecimalInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function SmsCreditsPage() {
  const { data: accounts, isLoading } = useSmsAccounts();
  const toggleAccount = useToggleSmsAccount();

  const handleToggle = async (account: SmsAccount, enabled: boolean) => {
    try {
      await toggleAccount.mutateAsync({ companyId: account.companyId, enabled });
      toast.success(enabled ? "Integração de SMS ativada" : "Integração de SMS desativada");
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao alterar a integração de SMS");
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">SMS</h1>
        <p className="text-sm text-muted-foreground">
          Ative a integração de SMS por empresa e controle o saldo disponível de cada uma. O
          provedor de SMS não expõe saldo — cada recarga registrada aqui informa a quantidade
          comprada e o custo total pago, e o sistema calcula o custo unitário daquela recarga.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !accounts || accounts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma empresa cadastrada ainda</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Integração de SMS</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.companyId}>
                  <TableCell className="font-medium">{account.companyName}</TableCell>
                  <TableCell>
                    <Switch
                      checked={account.enabled}
                      onCheckedChange={(checked) => handleToggle(account, checked)}
                      disabled={toggleAccount.isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {account.balance.toLocaleString("pt-BR")} SMS
                  </TableCell>
                  <TableCell className="text-right">
                    <RechargeSmsDialog
                      account={account}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Plus className="h-3.5 w-3.5" />
                          Recarregar
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function RechargeSmsDialog({ account, trigger }: { account: SmsAccount; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const recharge = useRechargeSmsAccount();

  useEffect(() => {
    if (!open) return;
    setQuantity("");
    setTotalCost("");
  }, [open]);

  const parsedQuantity = useMemo(() => {
    const parsed = Number(quantity.trim());
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  }, [quantity]);
  const parsedTotalCost = useMemo(() => parseDecimalInput(totalCost), [totalCost]);
  const unitCost =
    parsedQuantity && parsedTotalCost !== null && parsedTotalCost > 0
      ? parsedTotalCost / parsedQuantity
      : null;

  const handleConfirm = async () => {
    if (!parsedQuantity) {
      toast.error("Informe a quantidade de SMS comprada");
      return;
    }
    if (parsedTotalCost === null || parsedTotalCost <= 0) {
      toast.error("Informe o custo total pago nesta recarga");
      return;
    }
    try {
      await recharge.mutateAsync({ companyId: account.companyId, input: { quantity: parsedQuantity, totalCost: parsedTotalCost } });
      toast.success("Recarga registrada");
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao registrar a recarga");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Recarregar SMS — {account.companyName}</DialogTitle>
          <DialogDescription>
            Informe quantos SMS foram comprados e o valor total pago. O custo por SMS desta
            recarga é calculado automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sms-quantity">Quantidade de SMS</Label>
            <Input
              id="sms-quantity"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Ex: 1000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sms-total-cost">Valor total pago</Label>
            <Input
              id="sms-total-cost"
              inputMode="decimal"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              placeholder="Ex: 150,00"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {unitCost !== null
              ? `Custo unitário desta recarga: ${currencyFormatter.format(unitCost)} / SMS`
              : "Informe a quantidade e o valor total para calcular o custo unitário"}
          </p>
        </div>

        <DialogFooter>
          <Button onClick={handleConfirm} disabled={recharge.isPending}>
            {recharge.isPending ? "Registrando…" : "Confirmar recarga"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
